import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Paper } from "@mantine/core";
import AddDocumentButton from "@/components/UploadAdditionalDocuments/AddDocumentButton.tsx";
import {
  AppealSupportingDocument,
  AppealSupportingDocumentType,
  GetSupportingDocumentsForAppealQuery,
  GetSupportingDocumentsForAppealQueryVariables,
} from "@/__generated__/graphql.ts";
import { DocumentUploadModalMode } from "@/components/UploadAdditionalDocuments/types/modal.ts";
import { GET_SUPPORTING_DOCUMENTS_FOR_APPEAL } from "@/components/UploadAdditionalDocuments/graphql/requests.ts";
import { UploadedAdditionalDocument } from "@/components/UploadAdditionalDocuments/UploadedAdditionalDocument";
import { DocumentUploadModal } from "@/components/UploadAdditionalDocuments/modals/DocumentUploadModal";
import { ViewDocumentModal } from "@/components/UploadAdditionalDocuments/modals/ViewDocumentModal";
import { DeleteDocumentModal } from "@/components/UploadAdditionalDocuments/modals/DeleteDocumentModal";
import { FaqModal } from "@/components/UploadAdditionalDocuments/modals/FaqModal";
import Loader from "@/components/shared/Loader.tsx";
import { Alert } from "@/components/Alert";
import { isGlp1Condition } from "shared/src/data/medication-condition-pairs";

import styles from "./documents-tab.module.scss";

type ProviderAppealSupportingDocument = Omit<
  AppealSupportingDocument,
  "isIncludedInAppeal"
>;

enum ModalView {
  NONE = "NONE",
  UPLOAD = "UPLOAD",
  VIEW = "VIEW",
  DELETE = "DELETE",
  FAQ = "FAQ",
}

type NoModalView = {
  view: ModalView.NONE;
};

type UploadModalView = {
  view: ModalView.UPLOAD;
  mode: DocumentUploadModalMode;
  document: ProviderAppealSupportingDocument | null;
};

type ViewModalView = {
  view: ModalView.VIEW;
  document: ProviderAppealSupportingDocument;
};

type DeleteModalView = {
  view: ModalView.DELETE;
  document: ProviderAppealSupportingDocument;
};

type FaqModalView = {
  view: ModalView.FAQ;
  initialQuestion?: string;
};

type CurrentModalView =
  | NoModalView
  | UploadModalView
  | ViewModalView
  | DeleteModalView
  | FaqModalView;

const getDocumentIndexInDocumentsOfSameType = (
  document: ProviderAppealSupportingDocument,
  documents: ProviderAppealSupportingDocument[],
) => {
  if (document.documentType === AppealSupportingDocumentType.Other) {
    const documentsWithSameName = documents.filter(
      (doc) =>
        doc.documentType === document.documentType &&
        doc.otherDocumentTypeName === document.otherDocumentTypeName,
    );

    return documentsWithSameName.findIndex(
      (doc) => doc.documentId === document.documentId,
    );
  }

  const sameTypeDocuments = documents.filter(
    (doc) => doc.documentType === document.documentType,
  );

  return sameTypeDocuments.findIndex(
    (doc) => doc.documentId === document.documentId,
  );
};

interface Props {
  appealId: string;
  readOnly: boolean;
}

export function DocumentsTab({ appealId, readOnly = false }: Props) {
  const [currentModal, setCurrentModal] = useState<CurrentModalView>({
    view: ModalView.NONE,
  });
  const [pageError, setPageError] = useState<string | null>(null);

  const {
    data: supportingDocumentsData,
    loading: isRequestForSupportingDocumentsLoading,
    error: errorRequestingSupportingDocuments,
  } = useQuery<
    GetSupportingDocumentsForAppealQuery,
    GetSupportingDocumentsForAppealQueryVariables
  >(GET_SUPPORTING_DOCUMENTS_FOR_APPEAL, {
    variables: {
      appealId,
    },
  });

  const supportingDocuments =
    supportingDocumentsData?.getAppealById?.supportingDocuments;
  const condition = supportingDocumentsData?.getAppealById?.denial?.condition;

  const hasUploadedALetterOfMedicalNecessity =
    supportingDocuments?.some(
      (document) =>
        document.documentType ===
        AppealSupportingDocumentType.LetterOfMedicalNecessity,
    ) ?? false;

  const hasUploadedLabResults =
    supportingDocuments?.some(
      (document) =>
        document.documentType === AppealSupportingDocumentType.LabResults,
    ) ?? false;

  const lomnDocuments =
    supportingDocuments?.filter(
      (doc) =>
        doc.documentType ===
        AppealSupportingDocumentType.LetterOfMedicalNecessity,
    ) ?? [];

  const labResultsDocuments =
    supportingDocuments?.filter(
      (doc) => doc.documentType === AppealSupportingDocumentType.LabResults,
    ) ?? [];

  const otherDocuments =
    supportingDocuments?.filter(
      (doc) =>
        doc.documentType !==
          AppealSupportingDocumentType.LetterOfMedicalNecessity &&
        doc.documentType !== AppealSupportingDocumentType.LabResults,
    ) ?? [];

  const closeModal = () => {
    setCurrentModal({ view: ModalView.NONE });
  };

  const handleAddDocumentButtonClick = (documentType?: string) => () => {
    let mode = DocumentUploadModalMode.ADD;

    if (documentType === "LOMN") {
      mode = DocumentUploadModalMode.ADD_LOMN;
    } else if (documentType === "LAB_RESULTS") {
      mode = DocumentUploadModalMode.ADD_LAB_RESULTS;
    }

    setCurrentModal({
      view: ModalView.UPLOAD,
      mode,
      document: null,
    });
  };

  const handleCloseDocumentUploadModal = () => {
    closeModal();
  };

  const handleSubmitDocument = () => {};

  const handleOnViewDocument =
    (document: ProviderAppealSupportingDocument) => () => {
      setCurrentModal({
        view: ModalView.VIEW,
        document,
      });
    };

  const handleOnEditDocument =
    (document: ProviderAppealSupportingDocument) => () => {
      setPageError(null);
      setCurrentModal({
        view: ModalView.UPLOAD,
        mode: DocumentUploadModalMode.EDIT,
        document,
      });
    };

  const handleOnDeleteDocument =
    (document: ProviderAppealSupportingDocument) => () => {
      setPageError(null);
      setCurrentModal({
        view: ModalView.DELETE,
        document,
      });
    };

  const handleLoadDocumentFilesError = () => {
    setPageError(
      "There was an issue with loading the files of your document. Please try again later.",
    );
    closeModal();
  };

  useEffect(() => {
    if (errorRequestingSupportingDocuments) {
      setPageError(
        "There was an issue loading your documents. Please try again later.",
      );
    }
  }, [errorRequestingSupportingDocuments]);

  return (
    <Paper className={styles.container} p="md">
      <div className={styles.header}>
        <h2 className={styles.title}>Supporting Documents</h2>
        <p className={styles.subtitle}>
          Adding additional documents is not required but can strengthen the
          patient's appeal.
          <br />
          Learn about what you can attach, and why its important{" "}
          <button
            type="button"
            className={styles.faqLink}
            onClick={() => {
              setCurrentModal({
                view: ModalView.FAQ,
                initialQuestion: isGlp1Condition(condition)
                  ? "What documents are recommended for GLP-1 appeals?"
                  : undefined,
              });
            }}
          >
            here
          </button>
          .
        </p>
      </div>

      {pageError && (
        <Alert variant="error" title="An Error Occured">
          {pageError}
        </Alert>
      )}

      <div className={styles.uploadsContainer}>
        {!hasUploadedALetterOfMedicalNecessity && !readOnly && (
          <AddDocumentButton
            onClick={handleAddDocumentButtonClick("LOMN")}
            description="A statement supporting the patient's treatment is critical to their care. While not typically required, this can strengthen their case."
          >
            Doctor's letter of medical necessity
          </AddDocumentButton>
        )}

        {hasUploadedALetterOfMedicalNecessity &&
          supportingDocuments &&
          lomnDocuments.map((supportingDocument) => (
            <UploadedAdditionalDocument
              key={supportingDocument.documentId}
              type={supportingDocument.documentType}
              index={getDocumentIndexInDocumentsOfSameType(
                supportingDocument,
                supportingDocuments,
              )}
              onView={handleOnViewDocument(supportingDocument)}
              onEdit={handleOnEditDocument(supportingDocument)}
              onDelete={handleOnDeleteDocument(supportingDocument)}
              readOnly={readOnly}
            />
          ))}

        {!hasUploadedLabResults && !readOnly && (
          <AddDocumentButton
            onClick={handleAddDocumentButtonClick("LAB_RESULTS")}
            description="Add any lab or test results mentioned in the medical information section."
          >
            <span className={styles.buttonWithBadge}>
              Lab/Test results
              <span className={styles.recommendedBadge}>RECOMMENDED</span>
            </span>
          </AddDocumentButton>
        )}

        {hasUploadedLabResults &&
          supportingDocuments &&
          labResultsDocuments.map((supportingDocument) => (
            <UploadedAdditionalDocument
              key={supportingDocument.documentId}
              type={supportingDocument.documentType}
              index={getDocumentIndexInDocumentsOfSameType(
                supportingDocument,
                supportingDocuments,
              )}
              onView={handleOnViewDocument(supportingDocument)}
              onEdit={handleOnEditDocument(supportingDocument)}
              onDelete={handleOnDeleteDocument(supportingDocument)}
              readOnly={readOnly}
            />
          ))}

        {isRequestForSupportingDocumentsLoading && (
          <div className={styles.supportDocsLoading}>
            <Loader color="loader_almost_black" width="36" height="36" />
          </div>
        )}

        {supportingDocuments &&
          otherDocuments.map((supportingDocument) => (
            <UploadedAdditionalDocument
              key={supportingDocument.documentId}
              type={
                supportingDocument.documentType ===
                  AppealSupportingDocumentType.Other &&
                supportingDocument.otherDocumentTypeName
                  ? supportingDocument.otherDocumentTypeName
                  : supportingDocument.documentType
              }
              index={getDocumentIndexInDocumentsOfSameType(
                supportingDocument,
                supportingDocuments,
              )}
              onView={handleOnViewDocument(supportingDocument)}
              onEdit={handleOnEditDocument(supportingDocument)}
              onDelete={handleOnDeleteDocument(supportingDocument)}
              readOnly={readOnly}
            />
          ))}
        {!readOnly && (
          <AddDocumentButton
            onClick={handleAddDocumentButtonClick()}
            description="Upload lab results, medical records, other supporting documents."
          >
            Add other document
          </AddDocumentButton>
        )}
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.infoButton}
          onClick={() => {
            setCurrentModal({
              view: ModalView.FAQ,
              initialQuestion: "Are documents required?",
            });
          }}
        ></button>
      </div>

      {currentModal.view === ModalView.UPLOAD && (
        <DocumentUploadModal
          isOpen
          onClose={handleCloseDocumentUploadModal}
          onSubmitDocument={handleSubmitDocument}
          mode={currentModal.mode}
          document={currentModal.document}
          onLoadingDocumentFilesError={handleLoadDocumentFilesError}
          appealId={appealId}
        />
      )}

      {currentModal.view === ModalView.VIEW && (
        <ViewDocumentModal
          isOpen
          onClose={closeModal}
          files={currentModal.document.files}
          documentType={currentModal.document.documentType}
          otherDocumentTypeName={
            currentModal.document.otherDocumentTypeName ?? undefined
          }
        />
      )}

      {currentModal.view === ModalView.DELETE && (
        <DeleteDocumentModal
          appealId={appealId}
          document={currentModal.document}
          isOpen
          onClose={closeModal}
          onDeleteConfirmation={() => {}}
          onDeleteError={() =>
            setPageError(
              "There was an issue deleting the document. Please try again later.",
            )
          }
        />
      )}

      {currentModal.view === ModalView.FAQ && (
        <FaqModal
          initiallyOpenedQuestion={currentModal.initialQuestion}
          condition={condition}
          isOpen
          onClose={closeModal}
        />
      )}
    </Paper>
  );
}
